import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import HomeSidebar from './HomeSidebar'; // Adjust the path as necessary
import { FaCheckCircle } from "react-icons/fa"; // Import icon for completion
import Spinner from 'react-bootstrap/Spinner'; // Import Bootstrap Spinner

export { Home };

function Home() {
  const auth = useSelector((x) => x.auth.value);
  const [loading, setLoading] = useState(true); 
  const [showGeoCord, setShowGeoCord] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showDronePayload, setShowDronePayload] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [userActiveStatus, setUserActiveStatus] = useState(true); // Default to true initially
  const [userFirstName, setUserFirstName] = useState(''); // New state for firstname from API
  const [showButtons, setShowButtons] = useState(false);
  const [loadingActivate, setLoadingActivate] = useState(false); // Loading state for activate button
  const [loadingDeactivate, setLoadingDeactivate] = useState(false); // Loading state for deactivate button

  const baseUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const authData = localStorage.getItem('auth');    
    if (authData !== null) {
      try {
        const parsedData = JSON.parse(authData);
        
        // Check if master_password_used is true
        if (parsedData.master_password_used === true) {
          setShowButtons(true);  // Set state based on localStorage value
        }
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    } else {
      console.log("No auth data in localStorage");
    }
  }, []);
  
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const token = auth?.token;
        const response = await fetch(`${baseUrl}/currentuser`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (isMounted && result.profile) {
          const fetchedPolygons = result.profile["geometryfile"];
          const services = result.profile["services"];
          const description = result.profile["description"];
          const profile = result.profile;
          const termsAccepted = result.profile["terms_and_conditions"]; 
          const activeStatus = result.profile["user_active_status"]; 
          
          // Set user's first name from the API response
          setUserFirstName(profile["firstname"]);

          if (fetchedPolygons) {
            setShowGeoCord(true);
          }
          if (Array.isArray(services) && services.length > 0) {
            setShowServices(true);
          }
          if (description) {
            setShowDronePayload(true);
          }

          const requiredFields = ["firstname", "lastname", "ZIPCode", "address", "state", "company", "country", "phonenumber", "city"];

          const allFieldsCompleted = requiredFields.every(
            (field) => profile[field] && profile[field].trim() !== ""
          );

          if (allFieldsCompleted) {
            setShowList(true);
          }
          // Set terms as completed if accepted
          if (termsAccepted) {
            setShowTerms(true);
          }

          // Set user active status based on API response
          setUserActiveStatus(activeStatus);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [auth, baseUrl]);

  const handleUserStatusChange = async (status) => {
    const isActivating = status === true;

    // Update loading state based on button clicked
    if (isActivating) {
        setLoadingActivate(true);
    } else {
        setLoadingDeactivate(true);
    }

    try {
        const token = auth?.token;
        const response = await fetch(`${baseUrl}/update_user_status`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ user_active_status: status }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Update local state if needed
        setUserActiveStatus(status);
    } catch (error) {
        console.error("Error updating user status:", error);
    } finally {
        // Reset loading state
        if (isActivating) {
            setLoadingActivate(false);
        } else {
            setLoadingDeactivate(false);
        }
    }
};

  
  const allStepsCompleted = showGeoCord && showServices && showDronePayload && showList && showTerms;
  const showReviewMessage = allStepsCompleted && !userActiveStatus; // Determine if message should be shown

  const incompleteStepsList = [
    { step: "geoCord", show: showGeoCord },
    { step: "services", show: showServices },
    { step: "dronePayload", show: showDronePayload },
    { step: "list", show: showList },
    { step: "terms", show: showTerms }
  ].filter(item => !item.show).map(item => item.step);

  const showIncompleteMessage = incompleteStepsList.length > 0 && !allStepsCompleted;
  const showPartialCompletionMessage = incompleteStepsList.length < 5 && incompleteStepsList.length > 0;

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="home-container">
      {allStepsCompleted && <HomeSidebar />} {/* Show Sidebar only if all steps are completed */}
      <div className={`content ${allStepsCompleted ? "with-sidebar" : ""}`}>
        {showReviewMessage && (
          <div className="review-message">
            <p>Your account is under review</p>
          </div>
        )}
        {showIncompleteMessage && (
          <div className="incomplete-message">
            <p>Please complete your registration.</p>
          </div>
        )}
        <h1 className="welcome-message">
          Hi {userFirstName}! Welcome to UAVSphere
        </h1>
        <br />
        
        <div 
          className={`step-container ${showGeoCord ? "completed" : ""}`} 
          onClick={() => navigate("/maps")}
        >
          <div>Step 1 - Please select the location where you offer drone-related services.</div>
          {showGeoCord && (
            <div className="completed-icon-container">
              <FaCheckCircle className="completed-icon" />
              <span className="completed-text">Completed</span>
            </div>
          )}
        </div>
        <br />
        
        <div 
          className={`step-container ${showServices ? "completed" : ""}`} 
          onClick={() => navigate("/services")}
        >
          <div>Step 2 - Please select the services which you provide.</div>
          {showServices && (
            <div className="completed-icon-container">
              <FaCheckCircle className="completed-icon" />
              <span className="completed-text">Completed</span>
            </div>
          )}
        </div>
        <br />
        
        <div 
          className={`step-container ${showDronePayload ? "completed" : ""}`} 
          onClick={() => navigate("/dronepayload")}
        >
          <div>Step 3 - Please select the drone and payload you have.</div>
          {showDronePayload && (
            <div className="completed-icon-container">
              <FaCheckCircle className="completed-icon" />
              <span className="completed-text">Completed</span>
            </div>
          )}
        </div>
        <br />
        
        <div 
          className={`step-container ${showList ? "completed" : ""}`} 
          onClick={() => navigate("/list")}
        >
          <div>Step 4 - Please give more information about the company (Personal information if individual).</div>
          {showList && (
            <div className="completed-icon-container">
              <FaCheckCircle className="completed-icon" />
              <span className="completed-text">Completed</span>
            </div>
          )}
        </div>
        <br />
        
        <div 
          className={`step-container ${showTerms ? "completed" : ""}`} 
          onClick={() => navigate("/termsofuse")}
        >
          <div>Step 5 - Please accept the terms of use.</div>
          {showTerms && (
            <div className="completed-icon-container">
              <FaCheckCircle className="completed-icon" />
              <span className="completed-text">Completed</span>
            </div>
          )}
        </div>
        <br />

        {showButtons && (
          <>
           <button 
              onClick={() => handleUserStatusChange(true)} 
              disabled={userActiveStatus} // Disable if active
              className="btn btn-primary me-2"
          >
              {loadingActivate ? (
                  <Spinner animation="border" size="sm" />
              ) : (
                  "Activate"
              )}
          </button>
          <button 
              onClick={() => handleUserStatusChange(false)} 
              disabled={!userActiveStatus} // Disable if inactive
              className="btn btn-primary"
          >
              {loadingDeactivate ? (
                  <Spinner animation="border" size="sm" />
              ) : (
                  "Deactivate"
              )}
          </button>

          </>
        )}
      </div>
    </div>
  );
}
